import {
  isOtcProductSplittable,
  isRxFillRequestSplittable,
  isSplitInFlight,
  itemIdsInSplitFlight,
  OrderSplitStatus,
  OtcProductStatus,
  RxFillRequestStatus,
  splitOrderRejectionDetails,
  terminalOrderStatuses,
} from '@truepill/tpos-types'
import type { SplitFlightInput } from '@truepill/tpos-types'
import type { Order, OrderSplit, SplitLinkedOrder } from 'types'
import { PaymentType } from 'types'

export type SplitChipText = 'Split in progress' | 'Split needs attention' | "Split didn't complete"
export type SplitChip = { text: SplitChipText; splitOutOrderId?: string }

// A split entry as the tpos-types predicates read it
export const toSplitFlightInput = (split: OrderSplit): SplitFlightInput => ({
  status: split.status,
  orderId: split.orderId ?? undefined,
  requestedAt: new Date(split.requestedAt),
  rxFillRequestIds: split.rxFillRequestIds,
  otcProductIds: split.otcProductIds,
  splitOutOrderStatus: split.splitOutOrder?.status,
})

export const isSplitCancelled = (rejectionDetails?: { reasonCode?: string | null } | null): boolean =>
  rejectionDetails?.reasonCode === splitOrderRejectionDetails.reasonCode

export const splitItemIdsInFlight = (order: Pick<Order, 'splits'>, now = new Date()): Set<string> =>
  itemIdsInSplitFlight((order.splits ?? []).map(toSplitFlightInput), now)

// Items the user may pick: eligible by status and not in split flight
export const selectableSplitItemIds = (order: Order, now = new Date()): Set<string> => {
  const inFlight = splitItemIdsInFlight(order, now)
  const ids = new Set<string>()
  for (const fill of order.rxFillRequests) {
    if (isRxFillRequestSplittable(fill.status) && !inFlight.has(fill._id)) ids.add(fill._id)
  }
  for (const otc of order.otcProducts ?? []) {
    if (isOtcProductSplittable(otc.otcStatus) && !inFlight.has(otc._id)) ids.add(otc._id)
  }
  return ids
}

// Mirrors the mutation: whether a live item outside the selection and outside split flight stays on the order
export const leavesItemsOnOrder = (order: Order, selectedIds: Set<string>, now = new Date()): boolean => {
  const inFlight = splitItemIdsInFlight(order, now)
  const live = [
    ...order.rxFillRequests.filter(fill => fill.status !== RxFillRequestStatus.Cancelled).map(fill => fill._id),
    ...(order.otcProducts ?? []).filter(otc => otc.otcStatus !== OtcProductStatus.Cancelled).map(otc => otc._id),
  ]
  return live.some(id => !selectedIds.has(id) && !inFlight.has(id))
}

export const isOrderSplittable = (order: Order, now = new Date()): boolean =>
  !terminalOrderStatuses.includes(order.status) &&
  order.paymentType === PaymentType.cash &&
  selectableSplitItemIds(order, now).size > 0

const listsItem = (split: OrderSplit, itemId: string): boolean =>
  split.rxFillRequestIds.includes(itemId) || split.otcProductIds.includes(itemId)

const requestedAtMs = (split: OrderSplit): number => new Date(split.requestedAt).getTime()

// The entry that decides an item's chip: the one in split flight, else the newest one listing the item
const decidingSplit = (itemId: string, splits: OrderSplit[], now: Date): OrderSplit | undefined => {
  const listing = splits.filter(split => listsItem(split, itemId))
  return (
    listing.find(split => isSplitInFlight(toSplitFlightInput(split), now)) ??
    [...listing].sort((a, b) => requestedAtMs(b) - requestedAtMs(a))[0]
  )
}

// itemSplittable: whether the item's own status still allows a split; the stale chip is only shown while it does
export const splitChipForItem = (
  itemId: string,
  order: Pick<Order, 'splits'>,
  itemSplittable: boolean,
  now = new Date(),
): SplitChip | undefined => {
  const split = decidingSplit(itemId, order.splits ?? [], now)
  if (!split || split.status !== OrderSplitStatus.Pending) return undefined
  if (isSplitInFlight(toSplitFlightInput(split), now)) {
    return split.orderId
      ? { text: 'Split needs attention', splitOutOrderId: split.orderId }
      : { text: 'Split in progress' }
  }
  // Out of flight: the split-out order was cancelled (no chip) or nothing arrived in time
  if (split.orderId || !itemSplittable) return undefined
  return { text: "Split didn't complete" }
}

// A pending entry that names its split-out order is not a finished split, so only complete entries count
export const completedSplitOutOrders = (order: Pick<Order, 'splits'>): SplitLinkedOrder[] =>
  (order.splits ?? [])
    .filter(split => split.status === OrderSplitStatus.Complete)
    .flatMap(split => (split.splitOutOrder ? [split.splitOutOrder] : []))
